import { makeVar, useReactiveVar } from "@apollo/client";
import { useCallback, useRef } from "react";
import { useOnBeforeReload } from "~/hooks/use-on-before-reload";
import { useQueryString } from "~/hooks/use-query-string";
import type { G_FetchViewerVideoBookmarksQuery } from "~/lib/graphql";
import { useDeleteViewerVideoBookmarksOnMyBookmarkMutation } from "~/lib/graphql";
import { removeObjectKey } from "~/util/remove-object-key";

const isDeletedByIdsVar = makeVar<Record<string, boolean>>({});

export const useDeleteBookmark = () => {
  const isDeletedByIds = useReactiveVar(isDeletedByIdsVar);

  const { season } = useQueryString();
  // 画面から離脱する際(Unmount時)にStateがリセットされてしまうので、RefでMutation用の情報を保持する
  const isDeletedByIdsRef = useRef<typeof isDeletedByIds>({});
  isDeletedByIdsRef.current = isDeletedByIds;

  const [exec] = useDeleteViewerVideoBookmarksOnMyBookmarkMutation({
    update(cache, { data }) {
      if (!data?.deleteVideoBookmarks.mutationId) return;

      const ids = Object.keys(isDeletedByIdsRef.current);
      cache.modify({
        fields: {
          viewer(existing: G_FetchViewerVideoBookmarksQuery["viewer"], { readField }) {
            if (ids.length === 0) return;

            const filteredEdges = existing.videoBookmarks.edges.filter((args) => {
              const id = readField<string>("id", args.node);
              if (id === undefined) return;
              return !ids.includes(id);
            });
            return {
              ...existing,
              videoBookmarks: {
                ...existing.videoBookmarks,
                edges: filteredEdges,
              },
            };
          },
          video: (existing, args) => {
            if (ids.some((id) => id === args.readField("id", existing))) {
              cache.modify({
                id: cache.identify(existing),
                fields: {
                  hasBookmark: () => {
                    return false;
                  },
                },
              });
            }
            return existing;
          },
        },
      });
    },
  });

  const deleteBookmark = useCallback(
    (id: string) => {
      const prev = isDeletedByIdsVar();
      isDeletedByIdsVar({ ...prev, [id]: true });
    },
    [isDeletedByIdsVar]
  );

  const restoreBookmark = useCallback(
    (id: keyof typeof isDeletedByIds) => {
      const prev = isDeletedByIdsVar();
      isDeletedByIdsVar(removeObjectKey(prev, id));
    },
    [isDeletedByIdsVar]
  );

  const mutateDeleteBookmarks = () => {
    (async () => {
      const ids = Object.keys(isDeletedByIdsRef.current);
      if (ids.length === 0) return;

      await exec({
        variables: { input: { seasonIds: ids } },
      });
      isDeletedByIdsVar({});
    })();
  };

  const isDeletedById = useCallback(
    (id: string) => {
      return isDeletedByIds[id] ?? false;
    },
    [isDeletedByIds]
  );

  const onBeforeReload = () => {
    const isOpenedProductDetail = season !== undefined;
    if (isOpenedProductDetail) return;
    mutateDeleteBookmarks();
  };

  useOnBeforeReload({ onBeforeReload, deps: [season] });

  return { deleteBookmark, restoreBookmark, isDeletedById };
};
